import React from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { HorizontalBarChart } from '@/components/ui';
import { useAdminJobsStats } from '@/hooks/queries';

export function JobsDashboard() {
  const [windowVal, setWindowVal] = React.useState<'24h'|'7d'|'30d'>('24h');
  const { data, isLoading: loading } = useAdminJobsStats(windowVal);

  return (
    <PageContainer>
      <PageHeader title="Background Jobs" description="Queue health, throughput, runtime, and failure rates." />

      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm text-gray-600">Window:</label>
        <select value={windowVal} onChange={(e) => setWindowVal(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
          <option value="24h">24h</option>
          <option value="7d">7d</option>
          <option value="30d">30d</option>
        </select>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : !data ? (
        <div>No data</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent>
                <div className="text-sm text-gray-600">Average runtime</div>
                <div className="text-2xl font-semibold">{Math.round((data.runtime?.avgMs || 0))} ms</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-sm text-gray-600">Oldest queued at</div>
                <div className="text-2xl font-semibold">{data.oldestQueuedAt ? new Date(data.oldestQueuedAt).toLocaleString() : '—'}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-sm text-gray-600">Statuses</div>
                <div className="text-sm">{(data.byStatus || []).map((s: any) => `${s.status}: ${s.count}`).join(' · ') || '—'}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Average runtime by job type</CardTitle>
              <CardDescription>Sorted by highest average runtime</CardDescription>
            </CardHeader>
            <CardContent>
              {Array.isArray(data.runtimeByType) && data.runtimeByType.length > 0 ? (
                <HorizontalBarChart
                  unit="ms"
                  data={data.runtimeByType.map((r: any) => ({ label: `${r.type} (${r.count})`, value: r.avgMs }))}
                />
              ) : (
                <div className="text-sm text-gray-500">No completed jobs with runtime yet.</div>
              )}
            </CardContent>
          </Card>

          <Card variant="bordered">
            <CardHeader>
              <CardTitle className="text-base">Background jobs overview</CardTitle>
              <CardDescription>Registered types, schedules, and current activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Registered job types</div>
                  <div className="space-y-3">
                    {(data.jobTypes || []).map((jt: any) => (
                      <div key={jt.type} className="border rounded p-3">
                        <div className="text-sm font-medium text-gray-900">{jt.name} <span className="text-gray-500">({jt.type})</span></div>
                        <div className="text-sm text-gray-700 mt-1">{jt.description}</div>
                        <div className="text-xs text-gray-500 mt-1">Trigger: {jt.trigger}{jt.schedule ? ` · Schedule: ${jt.schedule}` : ''}</div>
                      </div>
                    ))}
                    {(!data.jobTypes || data.jobTypes.length === 0) && (
                      <div className="text-sm text-gray-500">No registered job types</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Current activity</div>
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500">As of {data.asOf ? new Date(data.asOf).toLocaleTimeString() : ''}</div>
                    <ul className="text-sm text-gray-700 list-disc pl-5">
                      {(data.runningNow || []).map((r: any) => (
                        <li key={r.type}>{r.type}: {r.count}</li>
                      ))}
                      {(!data.runningNow || data.runningNow.length === 0) && (
                        <li className="list-none text-gray-500">No jobs processing</li>
                      )}
                    </ul>
                  </div>
                  <div className="mt-6">
                    <div className="text-sm font-medium text-gray-700 mb-2">Schedules</div>
                    <ul className="text-sm text-gray-700">
                      <li><span className="text-gray-500">Worker:</span> {data.schedules?.worker || '—'}</li>
                      <li><span className="text-gray-500">Drafts TTL:</span> {data.schedules?.draftsTtl || '—'}</li>
                      <li><span className="text-gray-500">Embeddings:</span> {data.schedules?.embeddings || '—'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  );
}

function maxAvgMs(rows: Array<{ avgMs: number }>) {
  return rows.reduce((m, r) => (r.avgMs > m ? r.avgMs : m), 0) || 1;
}

function BarRow({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3">
      <div className="w-48 shrink-0 text-xs text-gray-700 truncate" title={label}>{label}</div>
      <div className="flex-1 h-4 bg-gray-100 rounded">
        <div
          className="h-4 bg-primary-500 rounded"
          style={{ width: `${pct}%` }}
          aria-label={`${label}: ${Math.round(value)} ms`}
        />
      </div>
      <div className="w-16 text-right text-xs text-gray-600">{Math.round(value)} ms</div>
    </div>
  );
}


